import React, {Fragment, useEffect, useState} from "react"
import {Garage, Service} from "@App/types"
import {GarageListItem} from "@App/Components/GarageListItem"
import {GarageListItemSkeleton} from "@App/Components/Skeletons/GarageListItemSkeleton"
import {GarageMap} from "@App/Components/GarageMap"
import {clearAllBodyScrollLocks, disableBodyScroll} from 'body-scroll-lock'
import {useDispatch, useSelector} from "react-redux"
import {actionCreators, State} from '@App/state'
import {useDeviceDimensions} from "@App/hooks/useDeviceDimensions"
import {NoGarageFound} from "@App/Components/NoGarageFound"
import {useGarages} from "@App/hooks/api/useGarages"
import {HintModal} from '@App/Components/HintModal'
import {useTranslation} from "react-i18next";
import {ServiceFAQs} from "@App/Components/ServiceFAQs";
import {ArrowDownIcon} from "@App/Components/common/Icons";
import {SortOrderType} from "@App/types/enums";
import {bindActionCreators} from "@reduxjs/toolkit";
import {OrganizationIsAyvens, OrganizationIsRepareo} from "@App/utils/conditionals/organization";
import {
    OrganizationIsNeitherRepareoNorDBConnect,
    OrganizationIsRepareoAndServiceIsInspection,
} from "@App/utils/conditionals/combinations";
import {useSearchParams} from "react-router-dom";
import {NonBookableHintModal} from "@App/Components/NonBookableHintModal";
import {DefaultSortingIsSmallestDistance} from "@App/utils/conditionals/sorting";
import {GarageIsBookable} from "@App/utils/conditionals/garages";

type GarageListProps = {
    toggleMap: boolean
    onToggleMap: (open: boolean) => void
    service?: Service
    onChange: (garage: Garage) => void
    className?: string
    mobileServiceFAQs?: boolean
}

export const GarageList = React.memo<GarageListProps>((props: GarageListProps) => {

    const organization = useSelector((state: State) => state.orders.organization)
    const place = useSelector((state: State) => state.orders.place)
    const service = useSelector((state: State) => state.orders.service)
    const sortBy = useSelector((state: State) => state.filters.sortBy)
    const brand = useSelector((state: State) => state.filters.brand)
    const {isMobile} = useDeviceDimensions()
    const {t} = useTranslation()
    const showAsNonBookable = useSelector((state: State) => state.filters.showAsNonBookable)
    const [searchParams] = useSearchParams()

    const dispatch = useDispatch();
    const {changeSortByFilter, changeShowAsNonBookableFilter} = bindActionCreators(actionCreators, dispatch)

    const [isInitialGarageList, setIsInitialGarageList] = useState(true)


    useEffect(() => {
        if (props.toggleMap && isMobile) {
            window.scrollTo(0, 0)
            disableBodyScroll(document.querySelector('#garage-map'))
        } else {
            clearAllBodyScrollLocks()
        }
        return () => {
            clearAllBodyScrollLocks()
        }
    })

    const {
        loading,
        fetchMoreLoading,
        calculation,
        garages,
        paginatorInfo,
        isFiltered,
        earliestAvailableGarage,
        bestReviewedGarage,
        nearestGarage,
        cheapestGarage,
        authorizedGarage,
        error,
        refetch,
        loadMore
    } = useGarages()

    // refetch if user used the browser back button
    useEffect(() => {
        if (!loading) {
            refetch()
        }
    }, [])

    useEffect(() => {
        if (isInitialGarageList && DefaultSortingIsSmallestDistance(organization) && garages && garages.filter(garage => garage.pivot?.preferred_partner).length > 0) {
            changeSortByFilter(SortOrderType.RECOMMENDED)
            setIsInitialGarageList(false)
        }
    }, [garages])

    if (error) {
        return <p>{t('error')}</p>
    }

    const style = {
        width: `${window.innerWidth}px`,
        height: `${window.innerHeight - (4 * 16)}px`
    }


    if (loading) {
        return (
            <Fragment>
                {[...Array(15)].map((e, i) => (<GarageListItemSkeleton key={i}/>))}
            </Fragment>
        )
    }

    if(searchParams.get('showAsNonBookable') == 'true') {
        changeShowAsNonBookableFilter(true)
    }

    const getRecommendedGarages = () => {
        return (garages && (
                <Fragment>
                    {garages.filter(garage => garage.pivot?.preferred_partner).map(garage => (
                        <GarageListItem
                            key={`garage-${garage.id}`}
                            service={service}
                            garage={garage}
                            showAsNonBookable={showAsNonBookable}
                            prefix={service.garage_prefix}
                            showCheckpoint={service.show_checkpoint}
                            fixedPrice={service.fixed_price}
                            onClick={props.onChange}
                            earliestAvailable={garage === earliestAvailableGarage}
                            nearest={garage === nearestGarage}
                            bestReviewed={garage === bestReviewedGarage}
                            cheapest={garage === cheapestGarage}
                            authorized={garage.authorizedBrands.includes(brand)}
                            calculation={calculation}
                        />
                    ))}
                    {OrganizationIsRepareo(organization) && authorizedGarage && (
                        <GarageListItem
                            key={`recommended-${authorizedGarage.id}`}
                            service={service}
                            garage={authorizedGarage}
                            showAsNonBookable={showAsNonBookable}
                            prefix={service.garage_prefix}
                            showCheckpoint={service.show_checkpoint}
                            fixedPrice={service.fixed_price}
                            onClick={props.onChange}
                            earliestAvailable={authorizedGarage === earliestAvailableGarage}
                            nearest={authorizedGarage === nearestGarage}
                            bestReviewed={authorizedGarage === bestReviewedGarage}
                            authorized={true}
                            cheapest={authorizedGarage === cheapestGarage}
                            highlighted={true}
                            calculation={calculation}
                        />
                    )}
                    {OrganizationIsRepareo(organization) && nearestGarage
                        && authorizedGarage !== nearestGarage && (
                            <GarageListItem
                                key={`recommended-${nearestGarage.id}`}
                                service={service}
                                garage={nearestGarage}
                                showAsNonBookable={showAsNonBookable}
                                prefix={service.garage_prefix}
                                showCheckpoint={service.show_checkpoint}
                                fixedPrice={service.fixed_price}
                                onClick={props.onChange}
                                earliestAvailable={nearestGarage === earliestAvailableGarage}
                                nearest={true}
                                bestReviewed={nearestGarage === bestReviewedGarage}
                                authorized={nearestGarage === authorizedGarage}
                                cheapest={nearestGarage === cheapestGarage}
                                highlighted={true}
                                calculation={calculation}
                            />
                        )}
                    {OrganizationIsRepareo(organization) && bestReviewedGarage
                        && bestReviewedGarage !== authorizedGarage
                        && bestReviewedGarage !== nearestGarage
                        && (
                            <GarageListItem
                                key={`recommended-${bestReviewedGarage.id}`}
                                garage={bestReviewedGarage}
                                showAsNonBookable={showAsNonBookable}
                                prefix={service.garage_prefix}
                                showCheckpoint={service.show_checkpoint}
                                fixedPrice={service.fixed_price}
                                service={service}
                                onClick={props.onChange}
                                earliestAvailable={bestReviewedGarage === earliestAvailableGarage}
                                nearest={bestReviewedGarage === nearestGarage}
                                bestReviewed={true}
                                authorized={bestReviewedGarage === authorizedGarage}
                                cheapest={bestReviewedGarage === cheapestGarage}
                                highlighted={true}
                                calculation={calculation}
                            />
                        )}
                    {OrganizationIsRepareo(organization) && earliestAvailableGarage
                        && earliestAvailableGarage !== authorizedGarage
                        && earliestAvailableGarage !== nearestGarage
                        && earliestAvailableGarage !== bestReviewedGarage && (
                            <GarageListItem
                                key={`recommended-${earliestAvailableGarage.id}`}
                                garage={earliestAvailableGarage}
                                showAsNonBookable={showAsNonBookable}
                                prefix={service.garage_prefix}
                                showCheckpoint={service.show_checkpoint}
                                fixedPrice={service.fixed_price}
                                service={service}
                                onClick={props.onChange}
                                earliestAvailable={true}
                                nearest={earliestAvailableGarage === nearestGarage}
                                bestReviewed={earliestAvailableGarage === bestReviewedGarage}
                                authorized={earliestAvailableGarage === authorizedGarage}
                                cheapest={earliestAvailableGarage === cheapestGarage}
                                highlighted={true}
                                calculation={calculation}
                            />
                        )}
                    {OrganizationIsRepareo(organization)
                        && cheapestGarage
                        && cheapestGarage !== authorizedGarage
                        && cheapestGarage !== nearestGarage
                        && cheapestGarage !== bestReviewedGarage
                        && cheapestGarage !== earliestAvailableGarage && (
                            <GarageListItem
                                key={`recommended-${cheapestGarage.id}`}
                                garage={cheapestGarage}
                                showAsNonBookable={showAsNonBookable}
                                prefix={service.garage_prefix}
                                showCheckpoint={service.show_checkpoint}
                                fixedPrice={service.fixed_price}
                                service={service}
                                onClick={props.onChange}
                                earliestAvailable={cheapestGarage === earliestAvailableGarage}
                                nearest={cheapestGarage === nearestGarage}
                                bestReviewed={cheapestGarage === bestReviewedGarage}
                                cheapest={true}
                                highlighted={true}
                                calculation={calculation}
                            />
                        )}
                </Fragment>
            )
        )
    }

    const getGarages = () => {
        return (garages && garages.map((garage, idx) => (
            <Fragment key={`garage-fragment-${garage.id}`}>
                {idx === 4 && service && service.pivot.faqs.length > 0 && (
                    <div className={'md:hidden'}>
                        <span className={'flex justify-center items-center text-neutral-700 text-xs py-2'}>
                            <ArrowDownIcon className={'w-5 h-5 mr-3'}/> Mehr Werkstätten unterhalb
                        </span>
                        <ServiceFAQs sidebar/>
                    </div>
                )}
                {/* Add a separator when the last bookable garage is displayed to differentiate between bookable and non-bookable garages*/}
                {([SortOrderType.BEST_REVIEWED, SortOrderType.EARLIEST_SLOT].includes(sortBy as SortOrderType)
                    && GarageIsBookable(garages[idx - 1])
                    && !GarageIsBookable(garage)) && (
                    <div className="text-lg font-bold font-serif mt-6 mb-3">
                        {t('listHints.furtherGaragesSortedByDistance')}
                    </div>
                )}
                <GarageListItem
                    key={`garage-${garage.id}`}
                    service={service}
                    garage={garage}
                    showAsNonBookable={showAsNonBookable}
                    prefix={service.garage_prefix}
                    showCheckpoint={service.show_checkpoint}
                    fixedPrice={service.fixed_price}
                    onClick={props.onChange}
                    earliestAvailable={garage === earliestAvailableGarage}
                    nearest={garage === nearestGarage}
                    bestReviewed={garage === bestReviewedGarage}
                    cheapest={garage === cheapestGarage}
                    authorized={garage.authorizedBrands.includes(brand)}
                    calculation={calculation}
                />
            </Fragment>
        )))
    }

    const modalAlreadyClicked = () => {
        try {
            return sessionStorage.getItem('HintModal') !== 'closed'
        } catch (error) {
            return true
        }
    }

    return (
        <div className={`${props.className || 'w-full'}`}>
            {modalAlreadyClicked() && (
                <Fragment>
                    {OrganizationIsNeitherRepareoNorDBConnect(organization) && (
                        <HintModal text={t('hint.text', {organization: organization.name.toUpperCase()})}
                                   service={service} organisation={organization}/>
                    )}

                    {OrganizationIsRepareoAndServiceIsInspection(organization, service) && (
                        <HintModal text={t('hint.repareo-inspection-text')} service={service} organisation={organization}/>
                    )}

                    {OrganizationIsAyvens(organization) && (
                        <NonBookableHintModal text={t('listHints.checkContract')}/>
                    )}
                    {showAsNonBookable && (
                        <NonBookableHintModal text={t('listHints.shownAsNonBookable')}/>
                    )}
                </Fragment>
            )}

            {garages && props.toggleMap && (
                <div className="mb-3 w-full">
                    <GarageMap
                        className={`md:min-h-full md:h-[48rem] md:w-full fixed md:relative bottom-0 left-0 z-20`}
                        style={isMobile ? style : null}
                        center={{lat: place.lat, lng: place.lon}}
                        garages={garages}
                        service={service}
                        onClose={() => props.onToggleMap(false)}
                        onSelectGarage={props.onChange}
                        zoom={11}/>
                </div>
            )}
            {sortBy === 'recommended' && !isFiltered && (
                <Fragment>
                    <div className="text-lg font-bold font-serif mb-3">
                        {t('listHints.ourTip')}
                    </div>
                    {getRecommendedGarages()}
                    <div className="text-lg font-bold font-serif mt-6 mb-3">
                        {t('listHints.sortedByDistance')}
                    </div>
                </Fragment>
            )}
            {getGarages()}
            {garages && garages.length === 0 && (
                <NoGarageFound/>
            )}
            {garages && paginatorInfo.hasMorePages && (
                <button disabled={fetchMoreLoading}
                        className={`bg-warning text-white w-full py-3 px-4 rounded mt-3 font-bold md:max-w-[340px] ${fetchMoreLoading ? 'animate-pulse' : ''}`}
                        onClick={loadMore}>
                    {fetchMoreLoading ? 'Lädt...' : 'Mehr Werkstätten anzeigen'}
                </button>
            )}
        </div>
    )
})
